import { Alert, Form, Input, Radio, Select, Typography } from 'antd';
import type { FormValues, SchemaField, FormSchema, FieldError } from '@/domain/schemaForm';
import { displayLabels, visibleFields } from '@/domain/schemaForm';
import DynamicSelect from './DynamicSelect';

const { Text } = Typography;

/**
 * 按 Airwallex 的动态 schema 渲染表单。与主站同一套逻辑，但有两处不同：
 * - **全部控件用 large 尺寸** —— 这个页面主要在手机上填，默认尺寸在触屏上太小；
 * - **兜底文案是英文的** —— 字段的 label / placeholder / description 由 Airwallex 下发，
 *   本来就是英文；只有我们自己补的那几句要跟着换。
 */
export default function SchemaForm({
  token,
  collectToken,
  schema,
  values,
  errors,
  disabled,
  lockedPaths,
  onChange,
}: {
  token: string;
  collectToken: string | null;
  schema: FormSchema;
  values: FormValues;
  errors: Record<string, string>;
  disabled?: boolean;
  /** 创建人预设过的字段（比如币种）：显示但不可改。 */
  lockedPaths?: Set<string>;
  /** 值变了就抛给调用方；要不要重新拉 schema 由调用方判断（见 Collect 页）。 */
  onChange: (path: string, value: string) => void;
}) {
  function renderControl(f: SchemaField) {
    const v = values[f.path] ?? '';
    const locked = disabled || lockedPaths?.has(f.path);
    const common = { disabled: locked, size: 'large' as const };

    switch (f.field.type) {
      case 'RADIO':
        return (
          <Radio.Group
            {...common}
            value={v}
            onChange={(e) => onChange(f.path, e.target.value)}
            options={(f.field.options ?? []).map((o) => ({ label: o.label, value: o.value }))}
            optionType="button"
            // 手机上按钮组要能换行，不然会被挤出屏幕
            style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
          />
        );

      case 'SELECT':
      case 'TRANSFER_METHOD':
        return (
          <Select
            {...common}
            showSearch
            value={v || undefined}
            placeholder={f.field.placeholder || 'Select'}
            optionFilterProp="label"
            onChange={(next) => onChange(f.path, next)}
            options={(f.field.options ?? []).map((o) => ({ label: o.label, value: o.value }))}
            style={{ width: '100%' }}
          />
        );

      case 'DYNAMIC_SELECT':
        return (
          <DynamicSelect
            token={token}
            collectToken={collectToken}
            field={f}
            value={v}
            values={values}
            disabled={locked}
            onChange={(next) => onChange(f.path, next)}
          />
        );

      case 'INPUT':
      default:
        return (
          <Input
            {...common}
            value={v}
            placeholder={f.field.placeholder || f.field.example || ''}
            onChange={(e) => onChange(f.path, e.target.value)}
          />
        );
    }
  }

  const fields = visibleFields(schema);
  const labels = displayLabels(fields);

  return (
    <Form layout="vertical" component="div" requiredMark>
      {fields.map((f) => {
        const err = errors[f.path];
        const hint = f.field.description || f.field.tip;
        return (
          <Form.Item
            key={f.path}
            label={labels[f.path]}
            required={f.required}
            validateStatus={err ? 'error' : undefined}
            help={
              err ??
              (hint ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {hint}
                </Text>
              ) : undefined)
            }
          >
            {renderControl(f)}
          </Form.Item>
        );
      })}

      {fields.length === 0 && <Alert type="info" title="Loading form…" />}
    </Form>
  );
}

export function toErrorMap(list: FieldError[]): Record<string, string> {
  const m: Record<string, string> = {};
  for (const e of list) m[e.path] = e.message;
  return m;
}
